// The corpus store: open, read, the atomic page commit, meta updates, and
// removal. One database per member; rows are the raw API envelopes, never
// decrypted here. The store never evicts (decision 28): a cache that dropped
// rows while search claimed completeness would make old notes look deleted.
// docs/plans/2026-09-02-next-steps-e2e-encryption.md, section 6.7; #1548.
import {
  deleteDatabase,
  listDatabases,
  openDatabase,
  readAll,
  readOne,
  storageUnavailable,
  transactionDone,
} from './idb';
import {
  CORPUS_DB_VERSION,
  META_STORE,
  NOTES_STORE,
  corpusDbName,
  emptyMeta,
  isCorpusDbName,
  registry,
  scopeKey,
  toRecord,
} from './schema';

const byId = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

function upgrade(db) {
  if (!db.objectStoreNames.contains(NOTES_STORE))
    db.createObjectStore(NOTES_STORE, { keyPath: 'id' });
  if (!db.objectStoreNames.contains(META_STORE))
    db.createObjectStore(META_STORE, { keyPath: 'scope' });
}

export function createCorpusStore(deps = {}) {
  // Resolved at call time: the web build and jsdom may have no IndexedDB, and
  // a plaintext member never opens a corpus, so nothing here runs for them.
  const getIdb = () => (deps.indexedDB !== undefined ? deps.indexedDB : globalThis.indexedDB);
  const getStorage = () => {
    try {
      return deps.storage !== undefined ? deps.storage : globalThis.localStorage;
    } catch (e) {
      return null;
    }
  };
  const connections = new Map();

  function open(memberId) {
    const idb = getIdb();
    if (!idb) return Promise.reject(storageUnavailable());
    const key = String(memberId);
    if (!connections.has(key)) {
      const opening = openDatabase(idb, corpusDbName(key), CORPUS_DB_VERSION, upgrade)
        .then(db => {
          // Another context deleting or upgrading the database must not be
          // blocked by this connection: logout from another tab, for one.
          db.onversionchange = () => {
            db.close();
            connections.delete(key);
          };
          db.onclose = () => connections.delete(key);
          registry.add(getStorage(), key);
          return db;
        })
        .catch(error => {
          connections.delete(key);
          throw error;
        });
      connections.set(key, opening);
    }
    return connections.get(key);
  }

  function close(memberId) {
    const key = String(memberId);
    const opening = connections.get(key);
    connections.delete(key);
    if (opening) opening.then(db => db.close()).catch(() => {});
  }

  function closeAll() {
    [...connections.keys()].forEach(close);
  }

  async function getMeta(memberId, scope) {
    if (!getIdb()) return emptyMeta(scope);
    const db = await open(memberId);
    const stored = await readOne(db, META_STORE, scopeKey(scope));
    return { ...emptyMeta(scope), ...(stored || {}) };
  }

  async function listRecords(memberId) {
    if (!getIdb()) return [];
    const db = await open(memberId);
    return (await readAll(db, NOTES_STORE)).sort(byId);
  }

  async function listNotes(memberId, scope) {
    const key = scopeKey(scope);
    return (await listRecords(memberId)).filter(r => r.scope === key).map(r => r.row);
  }

  async function getNote(memberId, id) {
    if (!getIdb()) return null;
    const db = await open(memberId);
    const record = await readOne(db, NOTES_STORE, String(id));
    return record ? record.row : null;
  }

  /**
   * Read-modify-write of one scope's meta row in a single transaction.
   * `mutate` returns the next row, or null to write nothing.
   */
  async function updateMeta(memberId, scope, mutate) {
    const db = await open(memberId);
    const tx = db.transaction([META_STORE], 'readwrite');
    const store = tx.objectStore(META_STORE);
    let next = null;
    const get = store.get(scopeKey(scope));
    get.onsuccess = () => {
      const current = { ...emptyMeta(scope), ...(get.result || {}) };
      next = mutate(current);
      if (next) store.put(next);
      else next = current;
    };
    await transactionDone(tx);
    return next;
  }

  /**
   * Begin a walk: move the scope's generation so a page still in flight from
   * an earlier walk commits nothing when it lands. A walk starting from the
   * beginning also zeroes the count the loading line reads.
   */
  function startWalk(memberId, scope) {
    return updateMeta(memberId, scope, meta => ({
      ...meta,
      generation: meta.generation + 1,
      loaded: meta.cursor === null && !meta.complete ? 0 : meta.loaded,
    }));
  }

  /** A write failed after the corpus claimed completeness: stop claiming it. */
  function clearComplete(memberId, scope) {
    return updateMeta(memberId, scope, meta =>
      meta.complete ? { ...meta, complete: false } : null,
    );
  }

  /**
   * Commit one page and its cursor advance in ONE transaction, so a crash
   * between them can neither lose a page nor count it twice. The commit is
   * refused (nothing written, `stale: true`) when the scope's generation or
   * cursor moved since the page was requested: a later walk or a reset owns
   * the scope now. A failing put aborts the transaction; the caller maps a
   * QuotaExceededError to `{ ok: false, reason: 'quota' }`.
   */
  function writePage(db, scope, { generation, cursorSent, items, nextCursor, total }) {
    return new Promise((resolve, reject) => {
      const tx = db.transaction([NOTES_STORE, META_STORE], 'readwrite');
      const notes = tx.objectStore(NOTES_STORE);
      const metaStore = tx.objectStore(META_STORE);
      let outcome = null;
      transactionDone(tx).then(
        () => resolve(outcome || { stale: true }),
        error => (outcome?.stale ? resolve(outcome) : reject(error)),
      );
      const get = metaStore.get(scopeKey(scope));
      get.onsuccess = () => {
        const meta = { ...emptyMeta(scope), ...(get.result || {}) };
        const moved =
          meta.generation !== generation || (meta.cursor ?? null) !== (cursorSent ?? null);
        if (moved || meta.complete) {
          outcome = { stale: true, meta };
          tx.abort();
          return;
        }
        let added = 0;
        items.forEach(item => {
          const probe = notes.get(String(item.id));
          probe.onsuccess = () => {
            if (probe.result === undefined) added += 1;
          };
        });
        items.forEach(item => notes.put(toRecord(item, scope)));
        const next = nextCursor ?? null;
        const updated = {
          ...meta,
          cursor: next,
          total: Number.isFinite(total) ? total : meta.total,
          loaded: meta.loaded + items.length,
          complete: next === null,
        };
        const putMeta = metaStore.put(updated);
        putMeta.onsuccess = () => {
          outcome = { stale: false, added, written: items.length, meta: updated };
        };
      };
    });
  }

  /** Store one row fetched by id (reconciliation), under the scope the row reports. */
  async function putNote(memberId, row, scope) {
    const db = await open(memberId);
    const tx = db.transaction([NOTES_STORE], 'readwrite');
    tx.objectStore(NOTES_STORE).put(toRecord(row, scope));
    await transactionDone(tx);
  }

  async function deleteNotes(memberId, ids) {
    if (!ids.length) return;
    const db = await open(memberId);
    const tx = db.transaction([NOTES_STORE], 'readwrite');
    const store = tx.objectStore(NOTES_STORE);
    ids.forEach(id => store.delete(String(id)));
    await transactionDone(tx);
  }

  /**
   * Forget a scope's progress so the next drain starts over, and (unless
   * `keepRows`) drop its rows. Moves the generation, so a page of the old walk
   * still in flight commits nothing.
   */
  async function resetScope(memberId, scope, { keepRows = false } = {}) {
    const db = await open(memberId);
    const key = scopeKey(scope);
    const tx = db.transaction([NOTES_STORE, META_STORE], 'readwrite');
    const notes = tx.objectStore(NOTES_STORE);
    const metaStore = tx.objectStore(META_STORE);
    const get = metaStore.get(key);
    get.onsuccess = () => {
      metaStore.put({ ...emptyMeta(scope), generation: (get.result?.generation ?? 0) + 1 });
      if (keepRows) return;
      const all = notes.getAll();
      all.onsuccess = () => {
        (all.result || []).filter(r => r.scope === key).forEach(r => notes.delete(r.id));
      };
    };
    await transactionDone(tx);
  }

  async function removeByName(idb, name) {
    const key = name.slice(corpusDbName('').length);
    const opening = connections.get(key);
    connections.delete(key);
    if (opening) await opening.then(db => db.close()).catch(() => {});
    await deleteDatabase(idb, name);
    registry.remove(getStorage(), key);
  }

  /** Delete one member's corpus. False when there is no IndexedDB to delete from. */
  async function removeCorpus(memberId) {
    const idb = getIdb();
    if (!idb) return false;
    await removeByName(idb, corpusDbName(String(memberId)));
    return true;
  }

  /**
   * Delete every corpus on this device (logout): the names the engine lists
   * under the prefix, plus every member the registry remembers, for engines
   * that cannot list. Resolves to the names removed; never throws.
   */
  async function removeAllCorpora() {
    const idb = getIdb();
    const storage = getStorage();
    if (!idb) return [];
    const listed = (await listDatabases(idb)) || [];
    const names = new Set([
      ...listed.filter(isCorpusDbName),
      ...registry.read(storage).map(corpusDbName),
    ]);
    const removed = [];
    for (const name of names) {
      try {
        await removeByName(idb, name);
        removed.push(name);
      } catch (e) {
        // Left for the next sign-out; the registry keeps its id for that.
      }
    }
    if (removed.length === names.size) registry.clear(storage);
    return removed;
  }

  return {
    open,
    close,
    closeAll,
    getMeta,
    listRecords,
    listNotes,
    getNote,
    startWalk,
    clearComplete,
    writePage,
    putNote,
    deleteNotes,
    resetScope,
    removeCorpus,
    removeAllCorpora,
    hasStorage: () => !!getIdb(),
  };
}
